// Roast list — primary entry point.
//
// Two modes, toggled by the `only_unrated` URL param:
//
//   - default — full list of roasts with filter bar (coffee, stars, date
//     range). Filters are not reactive; operator hits "Apply" when ready.
//   - unrated — delegate to list_unrated_roasts. Reached via the unrated
//     pill in the nav, a query-string chip replaces the filter bar so the
//     operator can clear it in one click.
//
// Every list handler filters the whole index and pages (issue #46). The
// page envelope carries `total`, `truncated` and `next_cursor`; the
// "Load more" control appends the next page under the current rows.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "../useApi";
import type { ListRoastsParams } from "../api";
import type { ListRoastsResponse, RoastSummary } from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { StarRating } from "../components/StarRating";
import { formatDate, formatTime } from "../format";
import { rowCoffee, rowTags } from "../rows";

interface Filters {
  coffee_id: string;
  min_stars: string; // "" | "1".."5"
  since: string;
  until: string;
}

const PAGE_SIZE = 50;

export function RoastList() {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyUnrated = searchParams.get("only_unrated") === "1";

  const [filters, setFilters] = useState<Filters>({
    coffee_id: "",
    min_stars: "",
    since: "",
    until: "",
  });
  const [summary, setSummary] = useState<string>("");
  const [roasts, setRoasts] = useState<RoastSummary[]>([]);
  const [page, setPage] = useState<Pick<ListRoastsResponse, "total" | "truncated" | "next_cursor"> | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = (cursor: string | null): Promise<ListRoastsResponse> =>
    onlyUnrated
      ? api.listUnratedRoasts({ limit: PAGE_SIZE, cursor })
      : api.listRoasts({ ...buildListArgs(filters), limit: PAGE_SIZE, cursor });

  const refresh = () => {
    setLoading(true);
    setErr(null);
    fetchPage(null)
      .then((r) => {
        setRoasts(r.roasts);
        setSummary(r.summary);
        setPage({ total: r.total, truncated: r.truncated, next_cursor: r.next_cursor });
      })
      .catch(setErr)
      .finally(() => setLoading(false));
  };

  const loadMore = () => {
    if (!page?.next_cursor) return;
    setLoadingMore(true);
    setErr(null);
    fetchPage(page.next_cursor)
      .then((r) => {
        setRoasts((prev) => [...prev, ...r.roasts]);
        setPage({ total: r.total, truncated: r.truncated, next_cursor: r.next_cursor });
      })
      .catch(setErr)
      .finally(() => setLoadingMore(false));
  };

  // Initial load + reload on unrated-mode toggle. In default mode we don't
  // refetch on filter keystrokes — operator hits "Apply" when ready.
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyUnrated]);

  const clearUnrated = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("only_unrated");
    setSearchParams(sp, { replace: true });
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>
        {onlyUnrated ? "Unrated roasts" : "Roasts"}
      </h1>

      {onlyUnrated ? (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="badge warn">only unrated</span>
          <button
            className="secondary"
            onClick={clearUnrated}
            style={{ minHeight: "auto", padding: "6px 12px" }}
          >
            Show all roasts
          </button>
        </div>
      ) : (
        <div className="filter-bar">
          <label>
            Coffee id
            <input
              value={filters.coffee_id}
              onChange={(e) => setFilters({ ...filters, coffee_id: e.target.value })}
              placeholder="e.g. ethiopia-gedeb"
            />
          </label>
          <label>
            Min stars
            <select
              value={filters.min_stars}
              onChange={(e) => setFilters({ ...filters, min_stars: e.target.value })}
            >
              <option value="">any</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  ≥ {n}
                </option>
              ))}
            </select>
          </label>
          <label>
            Since
            <input
              type="date"
              value={filters.since}
              onChange={(e) => setFilters({ ...filters, since: e.target.value })}
            />
          </label>
          <label>
            Until
            <input
              type="date"
              value={filters.until}
              onChange={(e) => setFilters({ ...filters, until: e.target.value })}
            />
          </label>
          <div style={{ alignSelf: "end" }}>
            <button onClick={refresh} disabled={loading}>
              {loading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      )}

      <ErrorBox error={err} />
      {summary && <p className="muted small">{summary}</p>}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Coffee</th>
              <th>Total</th>
              <th>Size</th>
              <th>Stars</th>
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {roasts.map((r) => (
              <tr key={r.roast_id}>
                <td>{formatDate(r.roast_date)}</td>
                <td>
                  <Link to={`/roasts/${encodeURIComponent(r.roast_id)}`}>
                    {rowCoffee(r)}
                  </Link>
                  <div className="small muted mono">{r.roast_id}</div>
                </td>
                <td>{formatTime(r.total_roast_time_s)}</td>
                <td>{r.batch_size_g != null ? `${r.batch_size_g}g` : "–"}</td>
                <td>
                  <StarRating value={r.stars ?? 0} />
                </td>
                <td>
                  {rowTags(r).map((t) => (
                    <span className="badge" key={t}>
                      {t}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
            {!loading && roasts.length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: "center" }}>
                  {onlyUnrated
                    ? "Everything's been rated. Nice."
                    : "No roasts match those filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {page && roasts.length > 0 && (
        <div className="pager">
          <span className="muted small">
            Showing {roasts.length} of {page.total}
          </span>
          {page.truncated && page.next_cursor && (
            <button
              className="secondary"
              onClick={loadMore}
              disabled={loadingMore}
              style={{ minHeight: "auto", padding: "6px 14px" }}
            >
              {loadingMore ? "Loading…" : `Load ${Math.min(PAGE_SIZE, page.total - roasts.length)} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Extract non-empty filter fields into the listRoasts arg shape.
function buildListArgs(f: Filters): ListRoastsParams {
  const args: ListRoastsParams = {};
  if (f.coffee_id) args.coffee_id = f.coffee_id;
  if (f.min_stars) args.min_stars = Number(f.min_stars);
  if (f.since) args.since = f.since;
  if (f.until) args.until = f.until;
  return args;
}
